import type { RequestCookie } from 'next/dist/compiled/@edge-runtime/cookies';
import type { GalleryImagesQuery, VideosQuery } from '../_graphql/__generated__/operations';
import { GALLERY_IMAGES_QUERY, VIDEOS_QUERY } from '../_graphql/gallery';
import { GRAPHQL_API_URL } from './shared';
import { payloadToken } from './token';

export type GalleryImageItem = NonNullable<
  NonNullable<NonNullable<GalleryImagesQuery['GalleryImages']>['docs']>[number]
>;

export type VideoItem = NonNullable<
  NonNullable<NonNullable<VideosQuery['Videos']>['docs']>[number]
>;

export type GalleryResponse<T> = Omit<NonNullable<GalleryImagesQuery['GalleryImages']>, 'docs'> & {
  docs: T[];
};

export type CollectionName = 'GalleryImages' | 'Videos';

export type GalleryContentFilters = {
  productTaxonomyIds?: string[];
  useCaseTaxonomyIds?: string[];
  searchText?: string;
  limit?: number;
  page?: number;
};

/**
 * Fetches filtered gallery content (images or videos) from the GraphQL API
 * @param collectionName - Either 'GalleryImages' or 'Videos'
 * @param filters - Object containing filter criteria
 * @param draft - Whether to include draft content
 * @param locale - Locale for the request
 * @returns Promise containing the filtered collection data
 */
export const fetchGalleryContent = async <T extends GalleryImageItem | VideoItem>(
  collectionName: CollectionName,
  filters: GalleryContentFilters = {},
  draft?: boolean,
  locale?: string,
): Promise<GalleryResponse<T>> => {
  const {
    productTaxonomyIds = [],
    useCaseTaxonomyIds = [],
    searchText,
    limit = 20,
    page = 1,
  } = filters;

  // Build the where clause
  const whereConditions: any = {};

  // Add taxonomy filters
  if (useCaseTaxonomyIds.length > 0) {
    whereConditions.useCases = {
      in: useCaseTaxonomyIds.map(id => parseInt(id, 10)),
    };
  }

  if (productTaxonomyIds.length > 0) {
    whereConditions.products = {
      in: productTaxonomyIds.map(id => parseInt(id, 10)),
    };
  }

  // Add text search filter
  if (searchText && searchText.trim()) {
    whereConditions.title = {
      contains: searchText.trim(),
    };
  }

  // Guard against undefined GRAPHQL_API_URL (e.g., during Docker builds without API access)
  if (!GRAPHQL_API_URL) {
    console.warn('[fetchGalleryContent] GRAPHQL_API_URL is not defined, returning empty response');
    return { docs: [], totalDocs: 0, totalPages: 0, page: 1, hasNextPage: false, hasPrevPage: false };
  }

  // Get authentication token if needed
  let token: RequestCookie | undefined;
  if (draft) {
    const { cookies } = await import('next/headers');
    token = (await cookies()).get(payloadToken);
  }

  // Select the appropriate query
  const query = collectionName === 'GalleryImages' ? GALLERY_IMAGES_QUERY : VIDEOS_QUERY;

  // Make the GraphQL request
  const response = await fetch(`${GRAPHQL_API_URL}/api/graphql/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token?.value && draft ? { Authorization: `JWT ${token.value}` } : {}),
    },
    cache: 'no-store',
    next: { tags: [collectionName.toLowerCase()] },
    body: JSON.stringify({
      query,
      variables: {
        where: Object.keys(whereConditions).length > 0 ? whereConditions : undefined,
        limit,
        page,
        draft,
        locale,
        ...(collectionName === 'Videos' ? { sort: '_order' } : {}),
      },
    }),
  });

  const result = await response.json();

  if (result.errors) {
    throw new Error(result?.errors?.[0]?.message ?? `Error fetching ${collectionName}`);
  }

  const data = result?.data?.[collectionName];

  if (process.env.LOG_GRAPHQL_QUERIES === 'true' || process.env.LOG_GRAPHQL_QUERIES === '1') {
    console.log(`${collectionName} query:`, {
      variables: { where: whereConditions, limit, page },
      results: data?.docs?.length || 0,
    });
  }

  return data as GalleryResponse<T>;
};

/**
 * Convenience function to fetch filtered gallery images
 */
export const fetchGalleryImages = async (
  filters: GalleryContentFilters = {},
  draft?: boolean,
  locale?: string,
): Promise<GalleryResponse<GalleryImageItem>> => {
  return fetchGalleryContent<GalleryImageItem>('GalleryImages', filters, draft, locale);
};

/**
 * Convenience function to fetch filtered videos
 */
export const fetchVideos = async (
  filters: GalleryContentFilters = {},
  draft?: boolean,
  locale?: string,
): Promise<GalleryResponse<VideoItem>> => {
  return fetchGalleryContent<VideoItem>('Videos', filters, draft, locale);
};
